/**
 * Version-skew detection for host-side MCP calls.
 *
 * A mounted plugin surface holds a capability token minted against the extension version that
 * was active when the page loaded. When the install is upgraded underneath it, the kernel stops
 * honouring that token: it rejects with `401` and
 * `X-Capability-Token-Rejection-Reason: ExtensionVersionMismatch`. Every other rejection reason is
 * terminal for the surface, but this one is recoverable — remounting against the new version fixes
 * it — which is why it is worth telling apart from an ordinary auth failure.
 *
 * Two signals are handled here, and the difference matters:
 *
 * - **Reactive**, from a rejection: the call already failed. Recovery costs the user a round trip
 *   and whatever in-page state the remount discards.
 * - **Proactive**, from the version stamped on a *successful* response: the surface learns the
 *   install moved while it is still working, and can remount at a moment of its choosing. Reads
 *   keep succeeding until the token's own version check catches up, so this is the path that
 *   avoids a visible failure altogether.
 *
 * The interpretation lives here rather than in each host application so that "which reason means
 * remount" is decided once, in code that can be tested, instead of being re-derived by every
 * embedder from HTTP trivia.
 */

/** Response header naming the extension version a request was served as. */
export const EXTENSION_VERSION_HEADER = "x-extension-version";

/** Response header naming the version a rejected surface must remount at. */
export const EXTENSION_ACTIVE_VERSION_HEADER = "x-extension-active-version";

/** Response header carrying the kernel's structured capability-token rejection reason. */
export const CAPABILITY_REJECTION_REASON_HEADER = "x-capability-token-rejection-reason";

/** The one rejection reason a surface can resolve by remounting. */
export const VERSION_MISMATCH_REASON = "extensionversionmismatch";

/**
 * What the host observed about an MCP response, beyond its payload. Both fields are optional
 * because the {@link McpHttpClient} contract predates them: an implementation that does not supply
 * them simply never reports skew, which is the previous behaviour rather than a new failure.
 */
export interface McpResponseMetadata
{
    /** HTTP status code, when the implementation surfaces it. */
    readonly status?: number;

    /** Response headers. Looked up case-insensitively, so any casing is fine. */
    readonly headers?: Readonly<Record<string, string>>;
}

/**
 * How skew came to light.
 *
 * - `"rejection"` — a call was refused because the surface's token is stale. Recovery costs a round
 *   trip and whatever in-page state the remount discards.
 * - `"response-stamp"` — a call succeeded, but the install has moved on. The surface can remount
 *   before anything fails, so this is the path worth preferring.
 */
export type VersionSkewSource = "rejection" | "response-stamp";

/**
 * The result of one host-side MCP call: the payload the plugin asked for, plus whatever the host
 * observed about the response itself.
 *
 * Named rather than inlined at the {@link McpHttpClient} declaration so hosts have a type to
 * implement against, and so the skew detector and the transports agree on one shape instead of
 * three structurally-compatible copies drifting apart.
 */
export interface McpHttpResponse extends McpResponseMetadata
{
    /** Whether the call succeeded. */
    readonly ok: boolean;

    /** The response payload, shaped by the request kind. */
    readonly data?: unknown;

    /** Failure message when {@link ok} is false. */
    readonly error?: string;

    /**
     * Optional machine-readable classification for a failure, mirroring the `mcpErrorCode` a client
     * can attach to a thrown error. Set it when the client knows why the call failed more precisely
     * than the status says - it takes precedence over {@link status} when the bridge classifies the
     * reply. Values outside the published vocabulary are ignored rather than forwarded.
     */
    readonly mcpErrorCode?: string;
}

/** A detected mismatch between the version a surface is running and the version now installed. */
export interface VersionSkew
{
    /**
     * The version to remount at, when the kernel named it. Undefined means skew is certain but the
     * target is not — the surface should re-read the extension descriptor rather than guess.
     */
    readonly activeVersion?: string;

    /** Which signal detected the skew. */
    readonly detectedFrom: VersionSkewSource;
}

/**
 * Case-insensitive header read. Hosts pass headers through from whatever HTTP client they use, and
 * casing varies by implementation (`fetch` lower-cases, some proxies and Node clients do not), so
 * matching exactly would make detection depend on the embedder's plumbing.
 */
function readHeader(headers: Readonly<Record<string, string>> | undefined, name: string): string | undefined
{
    if (!headers)
    {
        return undefined;
    }
    const direct = headers[name];
    if (direct !== undefined)
    {
        return direct;
    }
    const lowered = name.toLowerCase();
    for (const key of Object.keys(headers))
    {
        if (key.toLowerCase() === lowered)
        {
            return headers[key];
        }
    }
    return undefined;
}

function trimmedOrUndefined(value: string | undefined): string | undefined
{
    const trimmed = value?.trim();
    return trimmed !== undefined && trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Decides whether an MCP response means the surface is running against a version that is no longer
 * installed.
 *
 * @param response What the host observed: whether the call succeeded, plus any status and headers.
 * @param mountedExtensionVersion The version this surface mounted at. Required for proactive
 * detection — without it a response stamp cannot be compared to anything, and only rejections are
 * detectable.
 * @returns The skew, or `null` when the response says nothing about versions.
 */
export function detectVersionSkew(
    response: McpHttpResponse,
    mountedExtensionVersion?: string): VersionSkew | null
{
    const reason = trimmedOrUndefined(readHeader(response.headers, CAPABILITY_REJECTION_REASON_HEADER));
    if (reason !== undefined && reason.toLowerCase() === VERSION_MISMATCH_REASON)
    {
        return {
            activeVersion: trimmedOrUndefined(readHeader(response.headers, EXTENSION_ACTIVE_VERSION_HEADER)),
            detectedFrom: "rejection",
        };
    }

    // Proactive: only meaningful on a call that worked. A failed response's stamp says nothing
    // about the install having moved — the failure has its own cause, and treating it as skew
    // would remount the surface on every unrelated error.
    if (!response.ok)
    {
        return null;
    }

    const mounted = trimmedOrUndefined(mountedExtensionVersion);
    const served = trimmedOrUndefined(readHeader(response.headers, EXTENSION_VERSION_HEADER));
    if (mounted === undefined || served === undefined || served === mounted)
    {
        return null;
    }

    return { activeVersion: served, detectedFrom: "response-stamp" };
}
